import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useTenantStore } from '@/store/tenantStore';
import { ROUTES } from './routes';

/**
 * AccountGuard — mirrors the account checks in Flutter routing_config.dart
 * commonNavigation(), which run for every route (public ones included):
 *
 *   1. Signed in, email NOT verified, route is not verifyEmail
 *        → WebSignUpPage with showVerifyEmailPopup (the verify-email card)
 *   2. Signed in, email verified, tenant isMfaEnabled, !getIsMfaVerified(),
 *      route is not emailVerified
 *        → allow /duplicateAccount and /mfa, redirect everything else to /mfa
 *
 * /login and /signUp are exempt from both: they render AuthPage, which shows the
 * verify-email card itself, and useLogin / useSignup navigate to /mfa once the user
 * details have loaded — redirecting from here first would race them. A signed-in
 * user who opens either page is sent on to /home, which this guard then catches.
 */

const AUTH_PAGES: string[] = [ROUTES.LOGIN, ROUTES.SIGN_UP];

const EMAIL_EXEMPT_PATHS: string[] = [
  ...AUTH_PAGES,
  ROUTES.VERIFY_EMAIL,
];

const MFA_EXEMPT_PATHS: string[] = [
  ...AUTH_PAGES,
  ROUTES.MFA,
  ROUTES.DUPLICATE_ACCOUNT,
  ROUTES.EMAIL_VERIFIED,
];

export function AccountGuard() {
  const { isMfaVerified, user } = useAuthStore();
  const { content } = useTenantStore();
  const location = useLocation();

  if (!user) return <Outlet />;

  // Keep the ProfileInput / original destination for after verification
  const returnState = {
    ...((location.state as Record<string, unknown> | null) ?? {}),
    from: location,
  };

  if (!user.emailVerified) {
    if (EMAIL_EXEMPT_PATHS.includes(location.pathname)) return <Outlet />;
    return <Navigate to={ROUTES.SIGN_UP} replace state={returnState} />;
  }

  const isTenantMfaEnabled = content?.isMfaEnabled ?? false;
  if (isTenantMfaEnabled && !isMfaVerified && !MFA_EXEMPT_PATHS.includes(location.pathname)) {
    return (
      <Navigate
        to={ROUTES.MFA}
        replace
        state={{
          ...returnState,
          email: user.email ?? localStorage.getItem('userEmailId') ?? '',
        }}
      />
    );
  }

  return <Outlet />;
}
